<template>
  <AppLayout>
    <div class="fleet-crud-page">
      <div class="settings-shell">
        <aside class="panel settings-sidebar">
          <button
            v-for="section in sections"
            :key="section.kind"
            :class="['section-nav', { active: activeSection === section.kind }]"
            @click="activeSection = section.kind"
          >
            {{ section.label }}
          </button>
        </aside>

        <section v-if="currentSection" class="panel settings-content">
          <div class="table-head"></div>
          <div class="inline-create inline-create-row">
            <input
              v-model="drafts[currentSection.kind]"
              class="form-control form-control-sm"
              :placeholder="currentSection.placeholder"
              :disabled="createLoadingKind === currentSection.kind"
              @keyup.enter="createType(currentSection.kind)"
            />
            <button
              class="btn btn-sm btn-primary"
              :disabled="createLoadingKind === currentSection.kind"
              @click="createType(currentSection.kind)"
            >
              <span v-if="createLoadingKind === currentSection.kind" class="btn-loader" aria-hidden="true"></span>
              {{ createLoadingKind === currentSection.kind ? 'Adding...' : 'Add' }}
            </button>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in currentItems" :key="`${currentSection.kind}-${item.id}`">
                  <td>
                    <input v-model="item.name" class="form-control form-control-sm" />
                  </td>
                  <td class="action-cell">
                    <button
                      class="mini-btn"
                      :disabled="savingItemId === item.id || deletingItemId === item.id"
                      @click="updateType(currentSection.kind, item)"
                    >
                      <span v-if="savingItemId === item.id" class="btn-loader btn-loader-sm" aria-hidden="true"></span>
                      {{ savingItemId === item.id ? 'Saving...' : 'Save' }}
                    </button>
                    <button
                      class="mini-btn danger"
                      :disabled="savingItemId === item.id || deletingItemId === item.id"
                      @click="removeType(currentSection.kind, item)"
                    >
                      <span v-if="deletingItemId === item.id" class="btn-loader btn-loader-sm" aria-hidden="true"></span>
                      {{ deletingItemId === item.id ? 'Deleting...' : 'Delete' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="!currentItems.length">
                  <td colspan="2" class="empty">{{ currentSection.emptyText }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import fleetApi from '@/api/fleet';

export default {
  name: 'FleetSettings',
  components: { AppLayout },
  data() {
    return {
      vehicleTypes: [],
      fuelTypes: [],
      maintenanceTypes: [],
      vehicleStatuses: [],
      complianceTypes: [],
      activeSection: 'vehicle',
      sections: [
        {
          kind: 'vehicle',
          label: 'Vehicle Types',
          placeholder: 'e.g. Truck',
          emptyText: 'No vehicle types configured.',
        },
        {
          kind: 'fuel',
          label: 'Fuel Types',
          placeholder: 'e.g. Diesel',
          emptyText: 'No fuel types configured.',
        },
        {
          kind: 'maintenance',
          label: 'Maintenance Types',
          placeholder: 'e.g. Routine Service',
          emptyText: 'No maintenance types configured.',
        },
        {
          kind: 'status',
          label: 'Vehicle Status',
          placeholder: 'e.g. Available',
          emptyText: 'No vehicle status configured.',
        },
        {
          kind: 'compliance',
          label: 'Compliance Types',
          placeholder: 'e.g. Insurance',
          emptyText: 'No compliance types configured.',
        },
      ],
      drafts: {
        vehicle: '',
        fuel: '',
        maintenance: '',
        status: '',
        compliance: '',
      },
      createLoadingKind: null,
      savingItemId: null,
      deletingItemId: null,
    };
  },
  computed: {
    currentSection() {
      if (!Array.isArray(this.sections) || !this.sections.length) return null;
      return this.sections.find((section) => section.kind === this.activeSection) || this.sections[0];
    },
    currentItems() {
      if (this.activeSection === 'vehicle') return this.vehicleTypes;
      if (this.activeSection === 'fuel') return this.fuelTypes;
      if (this.activeSection === 'maintenance') return this.maintenanceTypes;
      if (this.activeSection === 'status') return this.vehicleStatuses;
      return this.complianceTypes;
    },
  },
  methods: {
    notifyError(err) {
      this.$toast?.error?.(err?.message || 'Request failed');
    },
    async loadAll() {
      try {
        const [vehicles, fuels, maintenance, statuses, compliances] = await Promise.all([
          fleetApi.listVehicleTypes(),
          fleetApi.listFuelTypes(),
          fleetApi.listMaintenanceTypes(),
          fleetApi.listVehicleStatuses(),
          fleetApi.listComplianceTypes(),
        ]);
        this.vehicleTypes = fleetApi.unwrapList(vehicles);
        this.fuelTypes = fleetApi.unwrapList(fuels);
        this.maintenanceTypes = fleetApi.unwrapList(maintenance);
        this.vehicleStatuses = fleetApi.unwrapList(statuses);
        this.complianceTypes = fleetApi.unwrapList(compliances);
      } catch (err) {
        this.notifyError(err);
      }
    },
    async createType(kind) {
      const name = (this.drafts[kind] || '').trim();
      if (!name) {
        this.$toast?.warning?.('Type name is required');
        return;
      }
      this.createLoadingKind = kind;
      try {
        if (kind === 'vehicle') await fleetApi.createVehicleType({ name });
        if (kind === 'fuel') await fleetApi.createFuelType({ name });
        if (kind === 'maintenance') await fleetApi.createMaintenanceType({ name });
        if (kind === 'status') await fleetApi.createVehicleStatus({ name });
        if (kind === 'compliance') await fleetApi.createComplianceType({ name });
        this.drafts[kind] = '';
        this.$toast?.success?.('Type created');
        await this.loadAll();
      } catch (err) {
        this.notifyError(err);
      } finally {
        this.createLoadingKind = null;
      }
    },
    async updateType(kind, item) {
      const name = (item?.name || '').trim();
      if (!name) {
        this.$toast?.warning?.('Type name is required');
        return;
      }
      this.savingItemId = item.id;
      try {
        if (kind === 'vehicle') await fleetApi.updateVehicleType(item.id, { name });
        if (kind === 'fuel') await fleetApi.updateFuelType(item.id, { name });
        if (kind === 'maintenance') await fleetApi.updateMaintenanceType(item.id, { name });
        if (kind === 'status') await fleetApi.updateVehicleStatus(item.id, { name });
        if (kind === 'compliance') await fleetApi.updateComplianceType(item.id, { name });
        this.$toast?.success?.('Type updated');
        await this.loadAll();
      } catch (err) {
        this.notifyError(err);
      } finally {
        this.savingItemId = null;
      }
    },
    async removeType(kind, item) {
      if (!item?.id || !window.confirm(`Delete ${item.name}?`)) return;
      this.deletingItemId = item.id;
      try {
        if (kind === 'vehicle') await fleetApi.deleteVehicleType(item.id);
        if (kind === 'fuel') await fleetApi.deleteFuelType(item.id);
        if (kind === 'maintenance') await fleetApi.deleteMaintenanceType(item.id);
        if (kind === 'status') await fleetApi.deleteVehicleStatus(item.id);
        if (kind === 'compliance') await fleetApi.deleteComplianceType(item.id);
        this.$toast?.success?.('Type deleted');
        await this.loadAll();
      } catch (err) {
        this.notifyError(err);
      } finally {
        this.deletingItemId = null;
      }
    },
  },
  mounted() {
    this.loadAll();
  },
};
</script>

<style scoped>
@import './fleet.shared.css';

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}

.inline-create {
  margin-bottom: 10px;
}

.inline-create-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.create-input {
  width: min(360px, 100%);
}

.inline-create-row .form-control {
  width: min(360px, 100%);
}

.settings-shell {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 0;
}

.settings-sidebar {
  padding: 14px 0 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: fit-content;
  background: transparent;
  box-shadow: none;
  border: none;
  border-radius: 0;
}

.settings-sidebar h3 {
  margin: 2px 0 8px;
}

.section-nav {
  width: 100%;
  text-align: left;
  border: 1px solid #d8e4f6;
  border-right: none;
  background: #fff;
  border-radius: 10px 0 0 10px;
  padding: 9px 14px;
  font-weight: 600;
  color: #2b4b69;
  cursor: pointer;
}

.section-nav.active {
  border-color: var(--fleet-accent);
  border-right: none;
  background: rgba(11, 110, 243, 0.09);
  color: var(--fleet-accent);
}

.settings-content {
  min-height: 520px;
  border-radius: 0 14px 14px 0;
}

.btn-loader {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  vertical-align: -2px;
  animation: button-spin 0.7s linear infinite;
}

.btn-loader-sm {
  width: 12px;
  height: 12px;
  margin-right: 5px;
}

@keyframes button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1100px) {
  .settings-shell {
    grid-template-columns: 1fr;
  }

  .settings-sidebar {
    position: sticky;
    top: 8px;
  }

  .inline-create-row {
    align-items: stretch;
    flex-direction: column;
  }

  .create-input {
    width: 100%;
  }
}
</style>
